import React, { useEffect, useState, useCallback } from 'react';
import { Camera, Mic, Bell, CheckCircle2, XCircle, ShieldCheck, RefreshCw } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from './ui/button';
import { Card } from './ui/card';

const STORAGE_KEY = 'tababa_permissions_granted';

const hasStoredAllGranted = () => {
  try { return localStorage.getItem(STORAGE_KEY) === 'true'; } catch { return false; }
};
const setStoredAllGranted = (v) => {
  try { localStorage.setItem(STORAGE_KEY, v ? 'true' : 'false'); } catch { /* ignore */ }
};

// Module-level components — extracted from the parent to keep the render tree
// stable (react/no-unstable-nested-components).
const StatusPill = ({ state, testId, t }) => {
  const isOk = state === 'granted';
  const isBlocked = state === 'denied';
  return (
    <span
      data-testid={testId}
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold ${
        isOk
          ? 'bg-emerald-100 text-emerald-700'
          : isBlocked
            ? 'bg-red-100 text-red-700'
            : 'bg-slate-100 text-slate-600'
      }`}
    >
      {isOk ? <CheckCircle2 size={12} /> : isBlocked ? <XCircle size={12} /> : null}
      {isOk
        ? t('مسموح', 'Allowed')
        : isBlocked
          ? t('غير مسموح', 'Not allowed')
          : t('لم يُطلب بعد', 'Not requested')}
    </span>
  );
};

const PermissionTile = ({ icon: Icon, label, sublabel, state, testId, t }) => (
  <div className={`flex items-center gap-3 p-4 rounded-2xl border ${state === 'granted' ? 'border-emerald-200 bg-emerald-50/50' : state === 'denied' ? 'border-red-200 bg-red-50/50' : 'border-slate-200 bg-white'}`}>
    <div className={`w-11 h-11 rounded-full flex items-center justify-center ${state === 'granted' ? 'bg-emerald-500 text-white' : state === 'denied' ? 'bg-red-500 text-white' : 'bg-slate-100 text-slate-600'}`}>
      <Icon size={20} />
    </div>
    <div className="flex-1 min-w-0">
      <div className="flex items-center justify-between gap-2">
        <p className="font-bold text-slate-800 text-sm">{label}</p>
        <StatusPill state={state} testId={testId} t={t} />
      </div>
      <p className="text-xs text-slate-500 mt-0.5">{sublabel}</p>
    </div>
  </div>
);

/**
 * ConsultationPermissionsGate — first-time consultation screen.
 *
 * Renders three permission tiles (camera, microphone, browser notifications)
 * with clear status pills and a single "Allow permissions" CTA. Once all
 * three are granted the gate persists that decision in localStorage and
 * unmounts itself so subsequent visits skip the wall.
 *
 * Props:
 *   - onGranted() — fired after all mandatory perms (camera+mic) are granted.
 *                    Notifications permission is optional (user may keep
 *                    the browser bell off; in-app bell still works).
 *   - videoRequired — when false (voice-only calls) skip camera check.
 */
export const ConsultationPermissionsGate = ({ onGranted, videoRequired = true }) => {
  const { t, language } = useLanguage();
  const isRTL = language === 'ar';

  const [cam, setCam] = useState('unknown');   // granted | denied | prompt | unsupported | unknown
  const [mic, setMic] = useState('unknown');
  const [notif, setNotif] = useState('unknown');
  const [busy, setBusy] = useState(false);

  // Poll the Permissions API for the current state (available in Chromium/FF).
  const probePermissions = useCallback(async () => {
    // Notifications
    if (typeof window !== 'undefined' && 'Notification' in window) {
      setNotif(Notification.permission || 'default');
    } else {
      setNotif('unsupported');
    }
    // Camera / mic via navigator.permissions (best-effort)
    if (typeof navigator !== 'undefined' && navigator.permissions?.query) {
      try {
        const c = await navigator.permissions.query({ name: 'camera' });
        setCam(c.state);
      } catch { setCam('prompt'); }
      try {
        const m = await navigator.permissions.query({ name: 'microphone' });
        setMic(m.state);
      } catch { setMic('prompt'); }
    } else {
      // Safari — Permissions API for camera/mic is unreliable. Default to prompt.
      setCam('prompt');
      setMic('prompt');
    }
  }, []);

  useEffect(() => {
    probePermissions();
  }, [probePermissions]);

  // If we've persisted a full grant, skip the gate immediately.
  useEffect(() => {
    if (hasStoredAllGranted()) {
      probePermissions();
      if (onGranted) onGranted();
    }
  }, [probePermissions, onGranted]);

  const allGranted = (videoRequired ? cam === 'granted' : true) && mic === 'granted';

  useEffect(() => {
    if (allGranted) {
      setStoredAllGranted(true);
      onGranted && onGranted();
    }
  }, [allGranted, onGranted]);

  const requestAll = async () => {
    setBusy(true);
    // Camera + mic — a single getUserMedia call prompts both when needed.
    try {
      const constraints = videoRequired ? { video: true, audio: true } : { audio: true };
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      // Immediately release the tracks — we only wanted the permission grant.
      stream.getTracks().forEach((tr) => tr.stop());
      setMic('granted');
      if (videoRequired) setCam('granted');
    } catch {
      // getUserMedia rejects → user denied or no device. Re-probe.
    }
    // Browser notifications
    if (typeof window !== 'undefined' && 'Notification' in window && Notification.permission === 'default') {
      try {
        const p = await Notification.requestPermission();
        setNotif(p);
        if (p === 'granted') {
          try {
            const { ensurePushSubscribed } = await import('./../hooks/usePushRegistration');
            ensurePushSubscribed();
          } catch { /* ignore */ }
        }
      } catch {/* ignore */}
    } else if (typeof window !== 'undefined' && 'Notification' in window) {
      setNotif(Notification.permission);
      if (Notification.permission === 'granted') {
        try {
          const { ensurePushSubscribed } = await import('./../hooks/usePushRegistration');
          ensurePushSubscribed();
        } catch { /* ignore */ }
      }
    }
    await probePermissions();
    setBusy(false);
  };

  const anyDenied = cam === 'denied' || mic === 'denied' || notif === 'denied';

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4 py-10 bg-gradient-to-br from-[#f4f8fc] to-[#e6f1fb]"
      dir={isRTL ? 'rtl' : 'ltr'}
      data-testid="permissions-gate"
    >
      <Card className="w-full max-w-lg p-6 md:p-8 border-0 shadow-xl rounded-3xl bg-white">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#22c8b8] to-[#1565c0] text-white flex items-center justify-center">
            <ShieldCheck size={22} />
          </div>
          <div>
            <h1 className="text-xl md:text-2xl font-extrabold text-slate-900">
              {t('صلاحيات الاستشارة', 'Consultation permissions')}
            </h1>
            <p className="text-sm text-slate-500 mt-0.5">
              {t('طبابة تحتاج إذنك للوصول إلى:', 'Tababa needs your permission to access:')}
            </p>
          </div>
        </div>

        <div className="mt-5 space-y-3" data-testid="permissions-tiles">
          {videoRequired && (
            <PermissionTile
              icon={Camera}
              label={t('الكاميرا', 'Camera')}
              sublabel={t('لعرض وجهك أثناء الاستشارة', 'To show your face during the consultation')}
              state={cam}
              testId="perm-status-camera"
              t={t}
            />
          )}
          <PermissionTile
            icon={Mic}
            label={t('المايكروفون', 'Microphone')}
            sublabel={t('للتحدث مع الطبيب', 'To talk to the doctor')}
            state={mic}
            testId="perm-status-microphone"
            t={t}
          />
          <PermissionTile
            icon={Bell}
            label={t('إشعارات المتصفح', 'Browser notifications')}
            sublabel={t('لإعلامك عندما يدخل الطبيب/المريض', 'To alert you when your doctor/patient joins')}
            state={notif}
            testId="perm-status-notifications"
            t={t}
          />
        </div>

        <Button
          type="button"
          onClick={requestAll}
          disabled={busy || allGranted}
          className="w-full mt-6 h-12 rounded-full bg-gradient-to-r from-[#22c8b8] via-[#1aa0d4] to-[#1565c0] text-white font-extrabold text-base shadow-lg hover:shadow-xl active:scale-[0.98] transition"
          data-testid="perm-request-button"
        >
          {allGranted
            ? t('جميع الصلاحيات مفعّلة', 'All permissions granted')
            : busy
              ? t('جاري الطلب...', 'Requesting...')
              : t('السماح بالصلاحيات', 'Allow permissions')}
        </Button>

        {anyDenied && (
          <div className="mt-4 p-4 rounded-xl bg-amber-50 border border-amber-200 text-sm text-amber-900" data-testid="perm-blocked-hint">
            <p className="font-bold mb-1">
              {t('تم رفض إحدى الصلاحيات', 'A permission was denied')}
            </p>
            <p className="text-xs leading-relaxed">
              {t(
                'افتح إعدادات الموقع في متصفحك (اضغط على أيقونة القفل بجانب الرابط) وفعّل الكاميرا والمايكروفون والإشعارات، ثم اضغط "حاول مجدداً".',
                'Open your browser site settings (click the lock icon beside the URL), enable Camera / Microphone / Notifications, then click "Try again".',
              )}
            </p>
            <Button
              type="button"
              variant="outline"
              onClick={probePermissions}
              className="mt-3 h-9 rounded-full text-amber-900 border-amber-300 hover:bg-amber-100"
              data-testid="perm-try-again"
            >
              <RefreshCw size={14} className="mr-1" />
              {t('حاول مجدداً', 'Try again')}
            </Button>
          </div>
        )}

        <p className="mt-5 text-[11px] text-slate-400 text-center">
          {t(
            'يمكنك تغيير هذه الصلاحيات لاحقاً من إعدادات المتصفح.',
            'You can change these permissions later from your browser settings.',
          )}
        </p>
      </Card>
    </div>
  );
};

export default ConsultationPermissionsGate;
